import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { finalize, lastValueFrom } from 'rxjs';

import { UsuarioDetalhado } from '../../../../models/UsuarioDetalhado';
import { UsuariosService } from '../../../../services/usuario/usuarios.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { getPaginatorIntl } from '../../../../shared/mat-paginator-intl';

interface UsuarioTable extends UsuarioDetalhado {
  selecionado?: boolean;
  bloqueado?: boolean;
}

@Component({
  selector: 'app-selecionar-usuarios.component',
  imports: [
    MatProgressSpinnerModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './selecionar-usuarios.component.html',
  styleUrl: './selecionar-usuarios.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MatPaginatorIntl, useFactory: getPaginatorIntl }],
})
export class SelecionarUsuariosComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  public isLoading = false;
  mustSaveSelected = false;
  usuarios = new MatTableDataSource<UsuarioTable>();
  listaCompleta: UsuarioTable[] = [];

  colunasTabela = [
    { id: 'nome', header: 'Nome', propriedade: 'nome' },
    { id: 'email', header: 'E-mail', propriedade: 'email' },
    {
      id: 'nomeFantasia',
      header: 'Empresa',
      propriedade: 'empresaNomeFantasia',
    },
  ];

  listaEmpresas: { id: number; nomeFantasia: string }[] = [];
  empresasSelecionadas: number[] = [];
  usuariosSelecionados: UsuarioDetalhado[] = [];
  usuariosSelecionadosLocal: UsuarioDetalhado[] = [];
  usuariosBloqueados: UsuarioDetalhado[] = [];

  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;

  get displayedColumns(): string[] {
    return ['selecionar', ...this.colunasTabela.map((coluna) => coluna.id)];
  }

  constructor(
    private usuariosService: UsuariosService,
    public dialogRef: MatDialogRef<SelecionarUsuariosComponent>,
    private snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  usuarioSelecionado = (usuario: UsuarioTable): boolean => {
    const usuarioSelecionadoManualmente =
      this.usuariosSelecionados.some((u) => u.id === usuario.id) ||
      this.usuariosSelecionadosLocal.some((u) => u.id === usuario.id);

    const grupoBuscandoUsuarios = this.data.grupos
      .filter((g: any) => g.buscaUsuarios)
      .some((g: any) => g.idGrupo === usuario.idGrupo);

    const empresasBuscandoUsuarios = this.data.empresas
      .filter((e: any) => e.buscaUsuarios)
      .some((e: any) => e.idEmpresa === usuario.idEmpresa);

    return (
      grupoBuscandoUsuarios ||
      empresasBuscandoUsuarios ||
      usuarioSelecionadoManualmente
    );
  };

  usuarioBloqueado = (usuario: UsuarioTable): boolean => {
    const grupoBuscandoUsuarios = this.data.grupos
      .filter((g: any) => g.buscaUsuarios)
      .some((g: any) => g.idGrupo === usuario.idGrupo);

    const empresasBuscandoUsuarios = this.data.empresas
      .filter((e: any) => e.buscaUsuarios)
      .some((e: any) => e.idEmpresa === usuario.idEmpresa);

    return grupoBuscandoUsuarios || empresasBuscandoUsuarios;
  };

  async ngOnInit(): Promise<void> {
    this.usuariosSelecionadosLocal = [...(this.data.usuarios || [])];
    this.isLoading = true;

    await this.buscarUsuariosAPI();
    this.configurarFiltros();
    this.configurarEmpresas();

    this.usuarios.data = this.usuarios.data.map((u) => ({
      ...u,
      selecionado: this.usuarioSelecionado(u),
      bloqueado: this.usuarioBloqueado(u),
    }));
  }

  ngAfterViewInit(): void {
    this.usuarios.paginator = this.paginator;
    this.usuarios.sort = this.sort;
  }

  async buscarUsuariosAPI() {
    try {
      const usuarios =
        await this.usuariosService.buscarUsuariosPorEmpresasEGrupos(
          this.data.empresasVinculadas,
          null
        );
      this.isLoading = false;

      this.listaCompleta = usuarios.map((u) => ({
        ...u,
        selecionado: this.usuarioSelecionado(u),
        bloqueado: this.usuarioBloqueado(u),
      }));

      // Separate blocked users
      this.usuariosBloqueados = this.listaCompleta
        .filter((u) => u.bloqueado)
        .map((u) => ({ ...u }));

      this.usuarios.data = [...this.listaCompleta];

      this.total = this.usuarios.data.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      this.snackbarService.error('Erro ao carregar usuários');
      this.usuarios.data = [];
      this.isLoading = false;
    }
  }

  private configurarEmpresas() {
    const empresasUnicas = new Map<
      number,
      { id: number; nomeFantasia: string }
    >();

    this.listaCompleta.forEach((u) => {
      if (u.idEmpresa && u.empresaNomeFantasia) {
        empresasUnicas.set(u.idEmpresa, {
          id: u.idEmpresa,
          nomeFantasia: u.empresaNomeFantasia, 
        });
      }
    });

    this.listaEmpresas = Array.from(empresasUnicas.values());
    this.empresasSelecionadas = [];
  }

  private configurarFiltros() {
    this.usuarios.filterPredicate = (
      data: UsuarioTable,
      filter: string
    ): boolean => {
      const termo = filter.trim().toLowerCase();
      return (
        (data.nome || '').toLowerCase().includes(termo) ||
        (data.email || '').toLowerCase().includes(termo)
      );
    };
  }

  filtrarPorEmpresa() {
    if (this.empresasSelecionadas.length === 0) {
      this.usuarios.data = [...this.listaCompleta];
    } else {
      this.usuarios.data = this.listaCompleta.filter(
        (u) => u.idEmpresa && this.empresasSelecionadas.includes(u.idEmpresa)
      );
    }
    this.total = this.usuarios.data.length;
    this.atualizarContagem(0, this.pageSize);
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number) {
    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  updateFieldText(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.usuarios.filter = query;
  }

  alternarUsuarioSelecionado(usuario: UsuarioTable, isChecked: boolean): void {
    if (this.usuarioBloqueado(usuario)) return;

    usuario.selecionado = isChecked;

    if (isChecked) {
      this.usuariosSelecionadosLocal.push(usuario);
    } else {
      this.usuariosSelecionadosLocal = this.usuariosSelecionadosLocal.filter(
        (u) => u.id !== usuario.id
      );
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    const usuariosConfirmados = this.usuariosSelecionadosLocal.filter(
      (u) => !this.usuarioBloqueado(u as UsuarioTable)
    );

    this.dialogRef.close([usuariosConfirmados]);
  }
}
